/**
 * Minimalist nail designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nail_looks`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const minimalistNailDesignsPack: ToilePack = {
    slug: 'minimalist-nail-designs',
    category: 'beauty',
    name: 'Minimalist nail designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'dot', label: 'One dot', subject: 'short round nails in sheer nude with a single small black dot near the cuticle of each nail'},
        {key: 'line', label: 'Thin line', subject: 'short square nails bare and glossy with one thin gold line across the base of each nail'},
        {key: 'negative', label: 'Negative space', subject: 'oval nails painted soft beige with a small triangle left bare at the tip of each nail, the natural nail showing through'},
    ],
    styles: [
        {key: 'linen', label: 'Linen', style: 'on undyed linen in soft morning daylight from camera-left, a calm bright balance with a faint shadow under the fingers', palette: 'Oatmeal linen and warm skin, the single mark the only accent', framing: 'The hand resting flat, seen from above, with generous empty space around it'},
        {key: 'desk', label: 'Desk', style: 'resting on a pale oak desk beside a closed plain notebook and a pen, soft window light from camera-right, a neutral mid-tone balance', palette: 'Pale oak and white paper around the hand', framing: 'A three-quarter view from above, the hand in the lower half of the frame'},
        {key: 'shadow', label: 'Leaf shadow', style: 'against white plaster in warm sun from camera-left, the soft shadow of a single leaf crossing the fingers, a crisp graphic balance of light and shade', palette: 'White plaster, warm skin and one green-grey leaf shadow', framing: 'The hand raised near-frontal against the plaster, the shadow falling across it'},
    ],
    directions: ['red dot', 'gold star', 'toe nails'],
    directionHint: 'try: red dot',
    seo: {
        title: 'Minimalist Nail Designs: One Dot, Thin Line, Negative Space',
        metaDescription: 'Nine minimalist nail designs: a single black dot, a thin gold line and a bare negative-space shape, in three settings.',
        h1: 'Minimalist nail designs: one dot, a thin line and negative space',
        tagline: "Nine simple sets: three one-idea designs, each shown on undyed linen, at a desk and with a leaf's shadow.",
        intro: 'Minimalist nail designs for people who want simple nails with one small idea, quick to do and easy to wear. The three designs are short round nails in sheer nude with one small black dot near the cuticle, short square nails with one thin gold line across the base, and oval beige nails with a small triangle left bare at the tip. Type a different dot color or line and the same single idea carries it.',
        useCases: [
            'a single dot for an office look',
            'a gold line for a wedding guest',
            'a bare negative-space tip for a quick set',
            'a simple set for a first manicure',
        ],
        faq: [
            {q: 'What is negative space on a nail?', a: 'A small shape left bare, so the natural nail shows through the paint.'},
            {q: 'Are these quick to do?', a: 'Each design uses one mark per nail. The pictures show the finished look, not how long it takes.'},
            {q: 'Can I ask for a red dot?', a: 'Yes. A color or mark you type is a request, not one of the nine pictures shown.'},
        ],
    },
}
